import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import SnackBar from '../components/Snackbar';
import CustomPagination from '../components/CustomPagination';
import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';
// import CreateButton from '../components/CreateButton';
import {
  APIFetchDigitalPasses,
  APIAssignDigitalPass,
  APIAdminFindUserByEmail,
  APIGetPassAssignments,
} from '../utils/apiCalled';
import { usePrivilege } from '../PrivilegeContext';
import AssignPassModal from '../components/dashboard/AssignPassModal';
import MorePasses from '../components/dashboard/MorePasses';
import { fDateTime } from '../utils/formatTime';

const TABLE_HEAD = [
  { id: 'serviceName', label: 'Pass Name', alignRight: false },
  { id: 'serviceCode', label: 'Service Code', alignRight: false },
  { id: 'serviceProviders', label: 'Provider', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'price', label: 'Price (MYR)', alignRight: false },
  { id: 'validityPeriodDays', label: 'Validity (Days)', alignRight: false },
  { id: 'assignmentCount', label: 'Assignments', alignRight: false },
  { id: 'updatedAt', label: 'Last Updated', alignRight: false },
  { id: 'actions', label: '', alignRight: true },
];

const ASSIGNMENTS_PAGE_SIZE = 10;

const tableStyle = {
  border: '0.063rem solid #EAECF0',
  borderTop: '0.094rem solid #EAECF0',
  borderBottom: '0.094rem solid #EAECF0',
  boxShadow: '0rem 0.063rem 0.188rem rgba(16, 24, 40, 0.1), 0rem 0.063rem 0.125rem rgba(16, 24, 40, 0.06)',
  borderRadius: '0.75rem',
};

const loadingStyle = {
  position: 'absolute',
  zIndex: 10,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
};

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export default function Passes() {
  const theme = useTheme();
  const { role } = usePrivilege();

  const [passes, setPasses] = useState([]);
  const [total, setTotal] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('serviceName');
  const [page, setPage] = useState(0);
  const [limit] = useState(10);
  const [filterName, setFilterName] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [variant, setVariant] = useState('info');
  const [mainMsg, setMainMsg] = useState('');
  const [succMsg, setSuccMsg] = useState('');
  const [errMsg, setErrMsg] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [selectedPass, setSelectedPass] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [searchingUser, setSearchingUser] = useState(false);
  const [assignUser, setAssignUser] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignmentsError, setAssignmentsError] = useState('');
  const [assignmentsPage, setAssignmentsPage] = useState(0);
  const [assignmentsTotal, setAssignmentsTotal] = useState(0);
  const [assignmentsLimit, setAssignmentsLimit] = useState(ASSIGNMENTS_PAGE_SIZE);
  const [assignmentsSearchQuery, setAssignmentsSearchQuery] = useState('');
  const searchTimeoutRef = useRef(null);
  const assignmentsSearchTimeoutRef = useRef(null);

  const canAssignPasses = role === 900 || role === 101 || role === 111;

  const fetchPasses = useCallback(async (preserveSnackbar = false) => {
    const params = {
      limit,
      skip: page * limit,
    };

    // Add search parameter if filterName has a value
    if (filterName && filterName.trim()) {
      params.searchString = filterName.trim();
    }

    try {
      const result = await APIFetchDigitalPasses(setLoading, params);
      if (result.status === 200) {
        const payload = result.data?.data || {};
        setPasses(payload.passes || []);
        setTotal(payload.total ?? payload.count ?? 0);
        if (!preserveSnackbar) {
          setVariant('success');
          setMainMsg('Passes refreshed');
          setSuccMsg(result.data?.message || 'Passes loaded successfully.');
          setErrMsg('');
          setOpen(false);
        }
      } else {
        throw result;
      }
    } catch (error) {
      const response = error?.response;
      setPasses([]);
      setTotal(0);
      setVariant('error');
      setMainMsg('Failed to load passes');
      const message = response?.data?.message || error?.message || 'Unable to retrieve passes.';
      setErrMsg(message);
      setSuccMsg('');
      setOpen(true);
    } finally {
      setLoading(false);
    }
  }, [limit, page, filterName]);

  // Initial load
  useEffect(() => {
    fetchPasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle pagination changes (page, limit) - fetch immediately
  useEffect(() => {
    // Skip if this is the initial render (filterName might trigger separately)
    if (page !== undefined) {
      fetchPasses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  // Debounced search effect - watches filterName and calls API after 600ms delay
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setPage(0); // Reset to first page when searching
      fetchPasses();
    }, 600);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterName]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    if (newPage < 0) {
      return;
    }
    setPage(newPage);
  };

  const filteredPasses = useMemo(
    () => applySortFilter(passes, getComparator(order, orderBy)),
    [passes, order, orderBy]
  );

  const fetchPassAssignments = useCallback(async (serviceCode, page = 0, searchString = '') => {
    if (!serviceCode) {
      return;
    }

    setAssignmentsError('');
    setAssignmentsLoading(true);

    const currentLimit = assignmentsLimit || ASSIGNMENTS_PAGE_SIZE;
    const skip = page * currentLimit;

    const params = {
      serviceCode,
      limit: currentLimit,
      skip,
    };

    // Add search parameter if searchString has a value
    if (searchString && searchString.trim()) {
      params.searchString = searchString.trim();
    }

    try {
      const result = await APIGetPassAssignments(setAssignmentsLoading, params);

      if (result.status === 200) {
        const payload = result.data?.data || {};
        const bundles = Array.isArray(payload.bundles) ? payload.bundles : [];
        const responseSkip = Number(payload.skip);
        const normalizedSkip = Number.isFinite(responseSkip) && responseSkip >= 0 ? responseSkip : skip;
        const inferredPage = Math.floor(normalizedSkip / currentLimit);
        if (inferredPage !== assignmentsPage) {
          setAssignmentsPage(inferredPage);
        }

        const rawTotal = Number(payload.total);
        const totalEstimate = Number.isFinite(rawTotal) ? rawTotal : (payload.count || bundles.length);

        setAssignments(bundles);
        setAssignmentsTotal(totalEstimate);
        setAssignmentsLimit(currentLimit);
      } else if (result.response?.status === 404) {
        setAssignments([]);
        setAssignmentsTotal(0);
        setAssignmentsError(result.response.data?.message || 'No assignments found for this pass.');
      } else {
        throw result;
      }
    } catch (error) {
      const response = error?.response;
      const message = response?.data?.message || error?.message || 'Unable to fetch pass assignments.';
      setAssignmentsError(message);
      setAssignments([]);
      setAssignmentsTotal(0);
    } finally {
      setAssignmentsLoading(false);
    }
  }, [assignmentsLimit, assignmentsPage, assignmentsSearchQuery]);

  const handleRowClick = (pass) => {
    setSelectedPass(pass);
    setAssignmentsPage(0);
    setShowDetails(true);
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedPass(null);
    setAssignments([]);
    setAssignmentsError('');
    setAssignmentsPage(0);
    setAssignmentsTotal(0);
    setAssignmentsLimit(ASSIGNMENTS_PAGE_SIZE);
    setAssignmentsSearchQuery('');
  };

  const handleOpenAssignModal = (pass) => {
    setSelectedPass(pass);
    setAssignUser(null);
    setAssignModalOpen(true);
  };

  const handleCloseAssignModal = useCallback(() => {
    setAssignModalOpen(false);
    setAssignUser(null);
  }, []);

  const handleSearchUser = useCallback(async (emailOrPhone) => {
    setSearchingUser(true);
    try {
      const result = await APIAdminFindUserByEmail(setSearchingUser, emailOrPhone);
      if (result.status === 200 && result.data?.data) {
        setAssignUser(result.data.data);
        return { success: true, user: result.data.data };
      }

      if (result.response?.status === 404) {
        setAssignUser(null);
        return { success: false, message: result.response.data?.message || 'User not found.' };
      }

      throw result;
    } catch (error) {
      setAssignUser(null);
      const response = error?.response;
      const message = response?.data?.message || error?.message || 'Unable to find user.';
      return { success: false, message };
    } finally {
      setSearchingUser(false);
    }
  }, []);

  const handleAssignPass = useCallback(async (payload) => {
    if (!payload.userId) {
      setVariant('error');
      setMainMsg('Assign Pass');
      setErrMsg('A user must be selected before assigning the pass.');
      setSuccMsg('');
      setOpen(true);
      return;
    }

    setLoading(true);
    try {
      const result = await APIAssignDigitalPass(setAssigning, payload);
      if (result.status === 200) {
        // Construct user name from assignUser state
        const userName = assignUser?.firstName || assignUser?.lastName
          ? `${assignUser.firstName || ''} ${assignUser.lastName || ''}`.trim()
          : 'User';
        
        // Get pass name from selectedPass
        const passName = selectedPass?.serviceName || 'the pass';
        
        setVariant('success');
        setMainMsg('Pass assigned successfully');
        setSuccMsg(`${userName} has been successfully assigned to ${passName}`);
        setErrMsg('');
        setOpen(true);
        setAssignModalOpen(false);
        setLoading(false);
        fetchPasses(true); // Pass true to preserve the snackbar
        if (showDetails && selectedPass?.serviceCode) {
          setAssignmentsPage(0);
          await fetchPassAssignments(selectedPass.serviceCode, 0, assignmentsSearchQuery);
        }
      } else {
        throw result;
      }
    } catch (error) {
      const response = error?.response;
      setVariant('error');
      setMainMsg('Failed to assign pass');
      const message = response?.data?.message || error?.message || 'Unable to assign pass.';
      setErrMsg(message);
      setSuccMsg('');
      setOpen(true);
    } finally {
      setAssigning(false);
      setLoading(false);
    }
  }, [fetchPasses, showDetails, selectedPass, fetchPassAssignments, assignUser]);

  useEffect(() => {
    if (showDetails && selectedPass?.serviceCode) {
      fetchPassAssignments(selectedPass.serviceCode, assignmentsPage, assignmentsSearchQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDetails, selectedPass, assignmentsPage]);

  // Debounced search effect for assignments - watches assignmentsSearchQuery and calls API after 600ms delay
  useEffect(() => {
    if (!showDetails || !selectedPass?.serviceCode) {
      return;
    }

    if (assignmentsSearchTimeoutRef.current) {
      clearTimeout(assignmentsSearchTimeoutRef.current);
    }
    assignmentsSearchTimeoutRef.current = setTimeout(() => {
      setAssignmentsPage(0); // Reset to first page when searching
      fetchPassAssignments(selectedPass.serviceCode, 0, assignmentsSearchQuery);
    }, 600);
    return () => {
      if (assignmentsSearchTimeoutRef.current) {
        clearTimeout(assignmentsSearchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentsSearchQuery, showDetails, selectedPass]);

  const handleSnackBarClose = () => {
    setOpen(false);
  };

  const handleResetUser = useCallback(() => {
    setAssignUser(null);
  }, []);


  return (
    <Page title="Passes">
      <Container maxWidth="xl" sx={{ mt: showDetails ? 0 : 12 }}>
        <SnackBar
          open={open}
          alert={variant}
          handleClose={handleSnackBarClose}
          errorMsg={errMsg}
          successMsg={succMsg}
          mainMsg={mainMsg}
        />

        {showDetails && selectedPass ? (
          <MorePasses
            selectedPass={selectedPass}
            handleClose={handleCloseDetails}
            handleAssignClick={() => handleOpenAssignModal(selectedPass)}
            canAssign={canAssignPasses}
            assignments={assignments}
            assignmentsLoading={assignmentsLoading}
            assignmentsError={assignmentsError}
            onRefreshAssignments={() =>
              fetchPassAssignments(selectedPass.serviceCode, assignmentsPage, assignmentsSearchQuery)
            }
            assignmentsPage={assignmentsPage}
            assignmentsLimit={assignmentsLimit}
            assignmentsTotal={assignmentsTotal}
            onChangeAssignmentsPage={(newPage) => setAssignmentsPage(newPage)}
            assignmentsSearchQuery={assignmentsSearchQuery}
            onAssignmentsSearchChange={setAssignmentsSearchQuery}
          />
        ) : (
          <>
            <Box sx={{ pb: 5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography variant="h3">Digital Passes</Typography>
              <Typography variant="body1" sx={{ color: theme.palette.grey[700] }}>
                Browse the available Trek Rides digital passes and assign them to riders.
              </Typography>
            </Box>

            {loading && <CircularProgress color="success" sx={loadingStyle} />}

            <Box sx={tableStyle}>
              <Box sx={{ mr: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <UserListToolbar
                  numSelected={0}
                  filterName={filterName}
                  onFilterName={handleFilterByName}
                  labelName="Search by pass or code"
                />
                {/* {canAssignPasses && (
                  <CreateButton type={'Passes'} handleOpen={() => {}} />
                )} */}
              </Box>
              <Scrollbar>
                <TableContainer sx={{ minWidth: 960 }}>
                  <Table>
                    <UserListHead
                      order={order}
                      orderBy={orderBy}
                      headLabel={TABLE_HEAD}
                      onRequestSort={handleRequestSort}
                    />
                    <TableBody>
                      {filteredPasses.map((row) => {
                        const {
                          _id,
                          serviceName,
                          serviceCode,
                          serviceProviders,
                          status,
                          price,
                          validityPeriodDays,
                          updatedAt,
                        } = row;

                        return (
                          <TableRow key={_id || serviceCode} hover onClick={() => handleRowClick(row)}>
                            <TableCell>
                              <Typography variant="subtitle2" sx={{ color: theme.palette.grey[900] }}>
                                {serviceName}
                              </Typography>
                              <Typography variant="body2" sx={{ color: theme.palette.grey[600] }}>
                                {row.description}
                              </Typography>
                            </TableCell>
                            <TableCell>{serviceCode}</TableCell>
                            <TableCell>{serviceProviders || '—'}</TableCell>
                            <TableCell>
                              <Chip
                                label={status || 'Unknown'}
                                color={status === 'ACTIVE' ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {price !== undefined ? `RM ${Number(price).toFixed(2)}` : '—'}
                            </TableCell>
                            <TableCell>{validityPeriodDays ? `${validityPeriodDays} days` : '—'}</TableCell>
                            <TableCell>
                              {(() => {
                                const count = row.assignmentCount ?? 0;
                                const intensity = count === 0 ? 0 : Math.min(0.15 + (count / 50) * 0.75, 0.9);
                                return (
                                  <Chip
                                    label={count}
                                    size="small"
                                    sx={{
                                      minWidth: 40,
                                      fontWeight: 600,
                                      backgroundColor: count > 0 
                                        ? `rgba(33, 150, 243, ${intensity})` 
                                        : theme.palette.grey[200],
                                      color: count > 0 && intensity > 0.5 
                                        ? '#fff' 
                                        : theme.palette.grey[800],
                                    }}
                                  />
                                );
                              })()}
                            </TableCell>
                            <TableCell>{updatedAt ? fDateTime(updatedAt) : '—'}</TableCell>
                            <TableCell align="right">
                              <Button
                                variant="contained"
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleOpenAssignModal(row);
                                }}
                                disabled={!canAssignPasses}
                              >
                                Assign
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      {filteredPasses.length === 0 && !loading && (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                            <Typography variant="body1" sx={{ color: theme.palette.grey[600] }}>
                              No passes found.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>
              <CustomPagination
                page={page}
                totalNum={total}
                rowsPerPage={limit}
                handleChangePage={handleChangePage}
              />
            </Box>
          </>
        )}
      </Container>

      <AssignPassModal
        open={assignModalOpen}
        onClose={handleCloseAssignModal}
        selectedPass={selectedPass}
        onAssign={handleAssignPass}
        isAssigning={assigning}
        onSearchUser={handleSearchUser}
        searchingUser={searchingUser}
        selectedUser={assignUser}
        onResetUser={handleResetUser}
      />
    </Page>
  );
}

